<template>
  <div>
    <v-menu bottom left>
      <template v-slot:activator="{ props }">
        <v-btn
          rounded
          color="primary"
          density="default"
          size="large"
          v-bind="props"
          append-icon="$dropdown"
        >
          {{ $t("reports.executiveSummaryReport.download.title") }}
        </v-btn>
      </template>
      <v-list>
        <v-list-item @click="downloadPdf()">
          <v-list-item-title>
            {{ $t(`reports.executiveSummaryReport.download.toPdf`) }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import type { PropType } from "vue";
import html2pdf from "html2pdf.js";
import { getReportVersion, useReportsStore } from "@/_store/reports.module";
import { getDefaultDisclaimerText } from "@/_helpers/subscription";
import { useDialogsStore } from "@/_store/dialogs.module";
import { ModalWidth } from "@/constants/modals";
import ErrorModal from "@/components/modals/ErrorModal.vue";
import { ReportType, REPORT_PDF_EXPORT_CLASS } from "@/constants/reports.ts";
import { isWorkspaceFrozenOrActionRestricted } from "@/_helpers/permissions.ts";
import { dialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";
import type { jsPDF } from "jspdf";
import { RolePermissionsScope, WorkspaceManagementScopeSections } from "@/constants/roles.ts";

const reportTypeOptionsMap: Record<
  ReportType,
  { prefix: string; margin: [number, number, number, number]; additionalHtml2PdfOptions?: object }
> = {
  [ReportType.EXECUTIVE_SUMMARY]: {
    prefix: "ExecutiveSummaryReport",
    margin: [10, 0, 10, 0],
    additionalHtml2PdfOptions: {
      pagebreak: { mode: ["css", "legacy"], avoid: [".page-break-avoid"] },
    },
  },
  [ReportType.MANAGED_SERVICES_SUMMARY]: {
    prefix: "ManagedServicesSummaryReport",
    margin: [10, 10, 10, 0],
  },
  [ReportType.SECURED_MESSAGES]: {
    prefix: "SecuredMessagesSummaryReport",
    margin: [10, 10, 10, 0],
    additionalHtml2PdfOptions: {
      pagebreak: { mode: ["css", "legacy"], avoid: [".page-break-avoid"] },
    },
  },
  [ReportType.DNS_SUMMARY]: {
    prefix: "DNSQueriesSummaryReport",
    margin: [0, 0, 0, 0],
    additionalHtml2PdfOptions: {
      pagebreak: {
        mode: ["css", "legacy"],
        before: "#report-table",
      },
    },
  },
  [ReportType.SECURITY_AWARENESS]: {
    prefix: "SecurityAwarenessTrainingReport",
    margin: [0, 0, 0, 0],
    additionalHtml2PdfOptions: {
      pagebreak: { mode: ["css", "legacy"], avoid: [".page-break-avoid"] },
    },
  },
  [ReportType.PHISHING_SIMULATION]: {
    prefix: "PhishingSimulationTrainingReport",
    margin: [0, 0, 0, 0],
    additionalHtml2PdfOptions: {
      pagebreak: { mode: ["css", "legacy"], avoid: [".page-break-avoid"] },
    },
  },
  [ReportType.WARNING_ONLY_EMAIL_EVENTS]: {
    prefix: "WarningOnlyEmailEventsReport",
    margin: [0, 0, 10, 0],
    additionalHtml2PdfOptions: {
      pagebreak: { mode: ["css", "legacy"], avoid: [".page-break-avoid"] },
    },
  },
  [ReportType.WARNING_ONLY_EMAIL_EVENTS_MSP]: {
    prefix: "WarningOnlyEmailEventsMspReport",
    margin: [0, 0, 10, 0],
    additionalHtml2PdfOptions: {
      pagebreak: { mode: ["css", "legacy"], avoid: [".page-break-avoid"] },
    },
  },
};

// Anything darker than this in any channel counts as content rather than as the card's white.
const WHITE_CHANNEL_THRESHOLD = 250;

/**
 * Drops a trailing page that carries nothing but the report card's bottom padding.
 *
 * html2pdf renders the report into one tall canvas and slices it into fixed-height strips, so
 * when the content ends just past a page boundary — the card's own bottom padding is enough to
 * do it — the final strip becomes a page with nothing on it. The strip's pixels are inspected
 * before anything is removed, so a page holding even one line of real content is always kept.
 * @param pdf - The assembled document, mutated in place.
 * @param canvas - The canvas html2pdf sliced into pages, or undefined if it is unavailable.
 * @param margin - The margins handed to html2pdf, in the jsPDF unit.
 * @returns Whether a page was removed.
 */
const dropBlankTrailingPage = (
  pdf: jsPDF,
  canvas: HTMLCanvasElement | undefined,
  margin: [number, number, number, number]
): boolean => {
  if (!canvas || pdf.getNumberOfPages() < 2) {
    return false;
  }
  const pageSize = pdf.internal.pageSize;
  const innerWidth = pageSize.getWidth() - margin[1] - margin[3];
  const innerHeight = pageSize.getHeight() - margin[0] - margin[2];
  // Mirrors html2pdf's own slicing arithmetic; any other formula would inspect the wrong band.
  const pxPageHeight = Math.floor(canvas.width * (innerHeight / innerWidth));
  const remainder = canvas.height % pxPageHeight;
  if (!remainder) {
    return false;
  }
  try {
    const context = canvas.getContext("2d");
    if (!context) {
      return false;
    }
    const { data } = context.getImageData(0, canvas.height - remainder, canvas.width, remainder);
    for (let i = 0; i < data.length; i += 4) {
      // Nothing was painted here, so there is nothing to see regardless of the colour channels.
      if (data[i + 3] === 0) {
        continue;
      }
      if (
        data[i] < WHITE_CHANNEL_THRESHOLD ||
        data[i + 1] < WHITE_CHANNEL_THRESHOLD ||
        data[i + 2] < WHITE_CHANNEL_THRESHOLD
      ) {
        return false;
      }
    }
  } catch {
    // A tainted canvas cannot be read back; keeping the page is the safe outcome.
    return false;
  }
  pdf.deletePage(pdf.getNumberOfPages());
  return true;
};

export default defineComponent({
  props: {
    reportType: {
      type: String as PropType<ReportType>,
      required: true,
    },
    beforeDownloadHook: {
      type: Function,
      required: false,
    },
    afterDownloadHook: {
      type: Function,
      required: false,
    },
    // Allows MSP view to provide their own restriction check
    // instead of the default workspace-management one.
    actionRestricted: {
      type: Boolean,
      default: undefined,
    },
  },
  setup(props) {
    const reportsStore = useReportsStore();
    const dialogsStore = useDialogsStore();
    const actionNotAllowed = computed(
      () =>
        props.actionRestricted ??
        isWorkspaceFrozenOrActionRestricted(
          RolePermissionsScope.WORKSPACE_MANAGEMENT,
          WorkspaceManagementScopeSections.REPORTS
        )
    );

    const downloadPdf = async () => {
      // Set before the before-download hook, which is where the charts are re-measured and
      // rasterised: a chart snapshot is only as correct as the width it was captured at. Removed
      // in a `finally` so the refusal path below cannot leave the report pinned. No pause is
      // needed between the two — the hook forces the charts to re-measure itself.
      const reportRoot = document.getElementById("report-card");
      reportRoot?.classList.add(REPORT_PDF_EXPORT_CLASS);
      try {
        if (props.beforeDownloadHook) {
          await props.beforeDownloadHook();
        }
        if (actionNotAllowed.value) {
          const errorModal = {
            width: ModalWidth.SMALL,
            header: undefined,
            content: {
              component: ErrorModal,
            },
            footer: null,
            data: {
              item: getDefaultDisclaimerText(),
            },
          };
          const dialogConfig = dialogsConfigConstructor(errorModal);
          dialogsStore.openDialog(dialogConfig);

          return;
        }
        const { margin, prefix, additionalHtml2PdfOptions } =
          reportTypeOptionsMap[props.reportType];
        const updatedVersion = getReportVersion();
        const filename = `${prefix}_${updatedVersion}.pdf`;
        const options = {
          margin,
          filename,
          jsPDF: { orientation: "landscape" as const, putTotalPages: true },
          html2canvas: { scale: 2, useCORS: true },
          ...additionalHtml2PdfOptions,
        };

        // This allows to add to pdf multiple elements that are siblings or distant relatives
        // and have other elements between them, but they need to be in the final pdf.
        // In this case report-card and report-table. Can be improved later to be configurable
        const tableElement = document.getElementById("report-table");
        const printContainer = document.createElement("div");
        if (reportRoot) {
          printContainer.appendChild(reportRoot.cloneNode(true));
        }
        if (tableElement) {
          printContainer.appendChild(tableElement.cloneNode(true));
        }

        const pdfFile = html2pdf().from(printContainer).set(options).toPdf();

        const pdf: jsPDF = await pdfFile.get("pdf");
        // Before numbering: the footer has to count the pages that survive, not the ones dropped.
        dropBlankTrailingPage(pdf, await pdfFile.get("canvas"), margin);
        const totalPages = pdf.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(10);
          pdf.setTextColor(100);
          pdf.text(
            "Page " + i + " of " + totalPages,
            pdf.internal.pageSize.getWidth() / 2.3,
            pdf.internal.pageSize.getHeight() - 5
          );
        }

        const pdfBlob = await pdfFile.output("blob");

        try {
          await reportsStore.uploadPdfReport(filename, pdfBlob, props.reportType);
          await pdfFile.save();
          reportsStore.$patch({ reportVersion: updatedVersion });
        } catch (e) {
          console.error(e);
        }
      } finally {
        reportRoot?.classList.remove(REPORT_PDF_EXPORT_CLASS);
        // In the `finally` for the same reason the class is: the permission refusal above returns
        // early and everything between can throw, while this hook is what puts the live charts
        // back in place of the `<img>` snapshots the before-hook swapped in and re-expands the
        // sections it collapsed. On the happy path only, a user without the REPORTS permission —
        // or any failed export — was left looking at dead placeholders until they navigated away.
        props.afterDownloadHook?.();
      }
    };

    return {
      downloadPdf,
      actionNotAllowed,
    };
  },
});
</script>

<style lang="scss" scoped>
:deep(*) {
  .icon-chevron-down-xs:before {
    color: inherit !important;
  }
}
</style>
